import { useSelected } from '../../../core';
import type { RenderElementProps } from '../../../core';
import type { PlaceholderPluginElement, PlaceholderPluginPropertyOptions } from '../types';
import type { GetRenderElementComponentType } from '../../../utils/getRenderElement';
import { unreachableCaseError } from 'types';
import { useReporterState } from '../../../hooks';
import styled from 'styled-components';
import { PlaceholderTemplateElement } from './PlaceholderTemplate';
import { PlaceholderReportElement } from './PlaceholderReport';
import { Node } from 'slate';
import { getPlaceholderName } from '../utils';
import { useCurrentWorklistItems } from 'hooks/useCurrentWorklistItems';
import { formatStudyInformationWithoutDate } from 'common/StudyInformationCard';

export type PlaceholderEditableProps = Readonly<
  RenderElementProps<PlaceholderPluginElement> & {
    ['data-editor-element']: string;
    pluginOptions: PlaceholderPluginPropertyOptions;
  }
>;

export const PlaceholderEditable: GetRenderElementComponentType<
  PlaceholderPluginPropertyOptions,
  PlaceholderPluginElement
> = ({
  pluginOptions: _pluginOptions,
  element,
  attributes,
  children,
  ...rest
}: PlaceholderEditableProps): React.ReactElement | null => {
  const { variant } = useReporterState();
  const selected = useSelected();
  const { currentWorklistItems } = useCurrentWorklistItems();
  const { placeholderID, workListItemSmid } = element;
  let emptyText;
  const isEmpty = Node.string(element) === '';
  const placeholderName = getPlaceholderName(placeholderID);
  const worklistItem = currentWorklistItems.find((item) => item.smid === workListItemSmid);

  if (isEmpty === true) {
    emptyText = `missing value for ${placeholderName}${
      currentWorklistItems.length > 1
        ? ' for ' +
          formatStudyInformationWithoutDate(
            worklistItem?.studyDescription,
            worklistItem?.accessionNumber
          )
        : ''
    }`;
  }

  switch (variant) {
    case 'report':
      return (
        <PlaceholderReportElement
          {...rest}
          attributes={attributes}
          selected={selected}
          element={element}
          emptyText={emptyText}
          testID={`placeholder-${placeholderName}`}
        >
          {children}
        </PlaceholderReportElement>
      );
    case 'fragment':
    case 'template':
      return (
        <PlaceholderTemplateElement
          {...rest}
          attributes={attributes}
          selected={selected}
          element={element}
          emptyText={emptyText}
          testID={`placeholder-${placeholderName}`}
        >
          {children}
        </PlaceholderTemplateElement>
      );
    case 'view':
      return null;

    default:
      return unreachableCaseError(variant);
  }
};

export const PlaceholderRenderer = styled(PlaceholderReportElement)`
  white-space: pre-wrap;
`;

const rendererAttributes = {
  'data-slate-node': 'element',
  ref: null,
} as const;

export const PlaceholderRenderElement = styled(
  ({ element, children }: { element: PlaceholderPluginElement; children: React.ReactNode }) => (
    <PlaceholderRenderer element={element} attributes={rendererAttributes}>
      {children}
    </PlaceholderRenderer>
  )
)`
  white-space: pre-wrap;
`;

export const PlaceholderReadOnlyRenderer = styled(
  ({ element, children }: { element: PlaceholderPluginElement; children: React.ReactNode }) => (
    <PlaceholderRenderer
      element={element}
      leftDelimiter=""
      rightDelimiter=""
      attributes={rendererAttributes}
    >
      {children}
    </PlaceholderRenderer>
  )
)`
  white-space: pre-wrap;
`;
